/* eslint-disable max-lines */

import { subscribe } from '@ember/instrumentation';
import { _backburner, run, scheduleOnce } from '@ember/runloop';
import { getOwnConfig, isTesting, macroCondition } from '@embroider/macros';
import * as Sentry from '@sentry/browser';
import { browserPerformanceTimeOrigin, GLOBAL_OBJ, timestampInSeconds } from '@sentry/utils';
import { getActiveTransaction } from '..';
function getSentryConfig() {
  const _global = GLOBAL_OBJ;
  _global.__sentryEmberConfig = _global.__sentryEmberConfig ?? {};
  const environmentConfig = getOwnConfig().sentryConfig;
  if (!environmentConfig.sentry) {
    environmentConfig.sentry = {
      browserTracingOptions: {}
    };
  }
  Object.assign(environmentConfig.sentry, _global.__sentryEmberConfig);
  return environmentConfig;
}
export function initialize(appInstance) {
  // Disable in fastboot - we only want to run Sentry client-side
  const fastboot = appInstance.lookup('service:fastboot');
  if (fastboot?.isFastBoot) {
    return;
  }
  const config = getSentryConfig();
  if (config['disablePerformance']) {
    return;
  }
  const performancePromise = instrumentForPerformance(appInstance);
  if (macroCondition(isTesting())) {
    window._sentryPerformanceLoad = performancePromise;
  }
}
function getBackburner() {
  if (_backburner) {
    return _backburner;
  }
  if (run.backburner) {
    return run.backburner;
  }
  return {
    on() {
      // noop
    },
    off() {
      // noop
    }
  };
}
function getTransitionInformation(transition, router) {
  const fromRoute = transition?.from?.name;
  const toRoute = transition?.to?.name || router.currentRouteName;
  return {
    fromRoute,
    toRoute
  };
}
function getLocationURL(location) {
  if (!location || !location.getURL || !location.formatURL) {
    return '';
  }
  const url = location.formatURL(location.getURL());
  if (location.implementation === 'hash') {
    return `${location.rootURL}${url}`;
  }
  return url;
}
export function _instrumentEmberRouter(routerService, routerMain, config, startTransaction, startTransactionOnPageLoad) {
  const {
    disableRunloopPerformance
  } = config;
  const location = routerMain.location;
  let activeTransaction;
  let transitionSpan;
  const url = getLocationURL(location);
  if (macroCondition(isTesting())) {
    routerService._sentryInstrumented = true;
    routerService._startTransaction = startTransaction;
  }
  if (startTransactionOnPageLoad && url) {
    const routeInfo = routerService.recognize(url);
    activeTransaction = startTransaction({
      name: `route:${routeInfo.name}`,
      op: 'pageload',
      origin: 'auto.pageload.ember',
      tags: {
        url,
        toRoute: routeInfo.name,
        'routing.instrumentation': '@sentry/ember'
      }
    });
  }
  const finishActiveTransaction = (_, nextInstance) => {
    if (nextInstance) {
      return;
    }
    activeTransaction?.finish();
    getBackburner().off('end', finishActiveTransaction);
  };
  routerService.on('routeWillChange', transition => {
    const {
      fromRoute,
      toRoute
    } = getTransitionInformation(transition, routerService);
    activeTransaction?.finish();
    activeTransaction = startTransaction({
      name: `route:${toRoute}`,
      op: 'navigation',
      origin: 'auto.navigation.ember',
      tags: {
        fromRoute,
        toRoute,
        'routing.instrumentation': '@sentry/ember'
      }
    });
    transitionSpan = activeTransaction?.startChild({
      op: 'ui.ember.transition',
      description: `route:${fromRoute} -> route:${toRoute}`,
      origin: 'auto.ui.ember'
    });
  });
  routerService.on('routeDidChange', () => {
    if (!transitionSpan || !activeTransaction) {
      return;
    }
    transitionSpan.finish();
    if (disableRunloopPerformance) {
      activeTransaction.finish();
      return;
    }
    getBackburner().on('end', finishActiveTransaction);
  });
  return {
    startTransaction
  };
}
function _instrumentEmberRunloop(config) {
  const {
    disableRunloopPerformance,
    minimumRunloopQueueDuration
  } = config;
  if (disableRunloopPerformance) {
    return;
  }
  let currentQueueStart;
  let currentQueueSpan;
  const instrumentedEmberQueues = ['actions', 'routerTransitions', 'render', 'afterRender', 'destroy'];
  getBackburner().on('begin', (_, previousInstance) => {
    if (previousInstance) {
      return;
    }
    const activeTransaction = getActiveTransaction();
    if (!activeTransaction) {
      return;
    }
    if (currentQueueSpan) {
      currentQueueSpan.finish();
    }
    currentQueueStart = timestampInSeconds();
    const processQueue = queue => {
      // Process this queue using the end of the previous queue.
      if (currentQueueStart) {
        const now = timestampInSeconds();
        const minQueueDuration = minimumRunloopQueueDuration ?? 5;
        if ((now - currentQueueStart) * 1000 >= minQueueDuration) {
          activeTransaction?.startChild({
            op: `ui.ember.runloop.${queue}`,
            origin: 'auto.ui.ember',
            startTimestamp: currentQueueStart,
            endTimestamp: now
          }).finish();
        }
        currentQueueStart = undefined;
      }

      // Setup for next queue

      const stillActiveTransaction = getActiveTransaction();
      if (!stillActiveTransaction) {
        return;
      }
      currentQueueStart = timestampInSeconds();
    };
    instrumentedEmberQueues.forEach(queue => {
      scheduleOnce(queue, null, processQueue, queue);
    });
  });
  getBackburner().on('end', (_, nextInstance) => {
    if (nextInstance) {
      return;
    }
    if (currentQueueSpan) {
      currentQueueSpan.finish();
      currentQueueSpan = undefined;
    }
  });
}
function processComponentRenderBefore(payload, beforeEntries) {
  const info = {
    payload,
    now: timestampInSeconds()
  };
  beforeEntries[payload.object] = info;
}
function processComponentRenderAfter(payload, beforeEntries, op, minComponentDuration) {
  const begin = beforeEntries[payload.object];
  if (!begin) {
    return;
  }
  const now = timestampInSeconds();
  const componentRenderDuration = now - begin.now;
  if (componentRenderDuration * 1000 >= minComponentDuration) {
    const activeTransaction = getActiveTransaction();
    activeTransaction?.startChild({
      op,
      description: payload.containerKey || payload.object,
      origin: 'auto.ui.ember',
      startTimestamp: begin.now,
      endTimestamp: now
    });
  }
}
function _instrumentComponents(config) {
  const {
    disableInstrumentComponents,
    minimumComponentRenderDuration,
    enableComponentDefinitions
  } = config;
  if (disableInstrumentComponents) {
    return;
  }
  const minComponentDuration = minimumComponentRenderDuration ?? 2;
  const beforeEntries = {};
  const beforeComponentDefinitionEntries = {};
  function _subscribeToRenderEvents() {
    subscribe('render.component', {
      before(_name, _timestamp, payload) {
        processComponentRenderBefore(payload, beforeEntries);
      },
      after(_name, _timestamp, payload, _beganIndex) {
        processComponentRenderAfter(payload, beforeEntries, 'ui.ember.component.render', minComponentDuration);
      }
    });
    if (enableComponentDefinitions) {
      subscribe('render.getComponentDefinition', {
        before(_name, _timestamp, payload) {
          processComponentRenderBefore(payload, beforeComponentDefinitionEntries);
        },
        after(_name, _timestamp, payload, _beganIndex) {
          processComponentRenderAfter(payload, beforeComponentDefinitionEntries, 'ui.ember.component.definition', 0);
        }
      });
    }
  }
  _subscribeToRenderEvents();
}
function _instrumentInitialLoad(config) {
  const startName = '@sentry/ember:initial-load-start';
  const endName = '@sentry/ember:initial-load-end';
  const {
    HAS_PERFORMANCE,
    HAS_PERFORMANCE_TIMING
  } = _hasPerformanceSupport();
  if (!HAS_PERFORMANCE) {
    return;
  }
  const {
    performance
  } = window;
  if (config.disableInitialLoadInstrumentation) {
    performance.clearMarks(startName);
    performance.clearMarks(endName);
    return;
  }

  // Split performance check in two so clearMarks still happens even if timeOrigin isn't available.
  if (!HAS_PERFORMANCE_TIMING || browserPerformanceTimeOrigin === undefined) {
    return;
  }
  const measureName = '@sentry/ember:initial-load';
  const startMarkExists = performance.getEntriesByName(startName).length > 0;
  const endMarkExists = performance.getEntriesByName(endName).length > 0;
  if (!startMarkExists || !endMarkExists) {
    return;
  }
  performance.measure(measureName, startName, endName);
  const measures = performance.getEntriesByName(measureName);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const measure = measures[0];
  const startTimestamp = (measure.startTime + browserPerformanceTimeOrigin) / 1000;
  const endTimestamp = startTimestamp + measure.duration / 1000;
  const transaction = getActiveTransaction();
  const span = transaction?.startChild({
    op: 'ui.ember.init',
    origin: 'auto.ui.ember',
    startTimestamp
  });
  span?.finish(endTimestamp);
  performance.clearMarks(startName);
  performance.clearMarks(endName);
  performance.clearMeasures(measureName);
}
function _hasPerformanceSupport() {
  // TS says that all of these methods are always available, but some of them may not be supported in older browsers
  // So we "pretend" they are all optional in order to be able to check this properly without TS complaining
  const _performance = window.performance;
  const HAS_PERFORMANCE = Boolean(_performance && _performance.clearMarks && _performance.clearMeasures);
  const HAS_PERFORMANCE_TIMING = Boolean(_performance.measure && _performance.getEntriesByName && browserPerformanceTimeOrigin !== undefined);
  return {
    HAS_PERFORMANCE,
    HAS_PERFORMANCE_TIMING
  };
}
export async function instrumentForPerformance(appInstance) {
  const config = getSentryConfig();
  // Maintaining backwards compatibility with config.browserTracingOptions, but passing it with Sentry options is preferred.
  const browserTracingOptions = config.browserTracingOptions || config.sentry.browserTracingOptions || {};
  const {
    BrowserTracing
  } = await import('@sentry/browser');
  const idleTimeout = config.transitionTimeout || 5000;
  const browserTracing = new BrowserTracing({
    routingInstrumentation: (customStartTransaction, startTransactionOnPageLoad) => {
      // eslint-disable-next-line ember/no-private-routing-service
      const routerMain = appInstance.lookup('router:main');
      let routerService = appInstance.lookup('service:router');
      if (routerService.externalRouter) {
        // Using ember-engines-router-service in an engine.
        routerService = routerService.externalRouter;
      }
      if (routerService._hasMountedSentryPerformanceRouting) {
        // Routing listens to route changes on the main router, and should not be initialized multiple times per page.
        return;
      }
      if (!routerService.recognize) {
        // Router is missing critical functionality to limit cardinality of the transaction names.
        return;
      }
      routerService._hasMountedSentryPerformanceRouting = true;
      _instrumentEmberRouter(routerService, routerMain, config, customStartTransaction, startTransactionOnPageLoad);
    },
    idleTimeout,
    ...browserTracingOptions
  });
  if (macroCondition(isTesting())) {
    const client = Sentry.getCurrentHub().getClient();
    if (client && client.getIntegrationById && client.getIntegrationById('BrowserTracing')) {
      // Initializers are called more than once in tests, causing the integrations to not be setup correctly.
      return;
    }
  }
  const client = Sentry.getCurrentHub().getClient();
  if (client && client.addIntegration) {
    client.addIntegration(browserTracing);
  }
  _instrumentEmberRunloop(config);
  _instrumentComponents(config);
  _instrumentInitialLoad(config);
}
export default {
  initialize
};